/**
 * client/src/features/income/components/AddIncome/IncomeTypeWizard.jsx
 * 
 * Income type selection wizard component.
 * Allows users to select income type and renders appropriate form based on selection.
 */
import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { 
  BriefcaseIcon, 
  HomeIcon, 
  LightBulbIcon,
  DocumentTextIcon, 
  CurrencyDollarIcon,
  BuildingOfficeIcon,
  XMarkIcon,
  ChartBarIcon,
  GiftIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import SalaryForm from './SalaryForm';
import BasicIncomeForm from './BasicIncomeForm';
import ContractorForm from './ContractorForm/index';
import { INCOME_TYPES } from '../../constants';

/**
 * Income Type Wizard Component
 * 
 * Guides the user through selecting an income type and filling out the appropriate form.
 * Also handles editing existing incomes with the same form interface.
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback to close the modal
 * @param {string} props.bucketId - ID of the bucket to add income to
 * @param {string} props.bucketName - Name of the bucket to add income to
 * @param {Function} props.onSuccess - Callback for successful form submission
 * @param {Object} props.income - Income object to edit (if editing mode)
 */
const IncomeTypeWizard = ({ isOpen, onClose, bucketId, bucketName, onSuccess, income }) => {
  // Determine if we're in edit mode
  const isEditMode = !!income;
  
  // State for tracking the current step in the wizard
  const [step, setStep] = useState(isEditMode ? determineFormTypeFromIncome(income) : 'type-selection');
  // State for selected income type
  const [selectedType, setSelectedType] = useState(isEditMode ? getNormalizedIncomeType(income) : null);
  
  // Helper function to determine which form to show based on income type
  function determineFormTypeFromIncome(income) {
    if (!income) return 'type-selection';
    
    const type = getNormalizedIncomeType(income);
    if (type === 'SALARY') return 'salary-form';
    if (type === 'CONTRACTOR' || type === 'CONTRACT' || type === '1099') return 'contractor-form';
    return 'basic-form';
  }
  
  // Helper function to normalize income type
  function getNormalizedIncomeType(income) {
    const type = (income.type || income.category || '').toUpperCase();
    if (type === 'CONTRACT') return 'CONTRACTOR';
    return type;
  }
  
  // Reset state when modal is opened
  useEffect(() => {
    if (isOpen) {
      if (isEditMode) {
        // If editing, go directly to the appropriate form
        setStep(determineFormTypeFromIncome(income));
        setSelectedType(getNormalizedIncomeType(income));
      } else {
        // If adding new, start with type selection
      setStep('type-selection');
      setSelectedType(null);
    }
    }
  }, [isOpen, income, isEditMode]);
  
  // Handle income type selection
  const handleTypeSelect = (typeId) => {
    setSelectedType(typeId);
    
    // Move to the appropriate form based on income type
    if (typeId === 'SALARY') {
      setStep('salary-form');
    } else if (typeId === 'CONTRACT' || typeId === 'CONTRACTOR' || typeId === '1099') {
      setStep('contractor-form');
    } else {
      setStep('basic-form');
    }
  };
  
  // Handle back button
  const handleBack = () => {
    if (isEditMode) {
      // If editing, closing the modal when back is clicked
      onClose();
    } else {
      // If adding new, go back to type selection
    setStep('type-selection');
    }
  };
  
  // Handle form submission success
  const handleSuccess = () => {
    if (onSuccess) onSuccess();
    onClose();
  };
  
  // Get the title based on current step and edit mode
  const getStepTitle = () => {
    if (isEditMode) {
      // When editing, show "Edit [Income Name] ([Type])"
      const incomeName = income?.name || 'Income';
      const incomeType = INCOME_TYPES.find(type => type.id === selectedType)?.title || 'Income';
      return `Edit ${incomeName} (${incomeType})`;
    }
    
    if (step === 'type-selection') {
      return `Add Income to ${bucketName || 'Bucket'}`;
    } else if (step === 'salary-form') {
      return 'Add Salary Income';
    } else if (step === 'contractor-form') {
      return 'Add Contract/1099 Income';
    } else if (step === 'basic-form') {
      const selectedTypeInfo = INCOME_TYPES.find(type => type.id === selectedType);
      return `Add ${selectedTypeInfo?.title || 'Income'}`;
    }
    return 'Add Income';
  };
  
  // Render the current step
  const renderStep = () => {
    switch (step) {
      case 'type-selection':
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-500 mb-4">Select your income type</p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {INCOME_TYPES.map((type) => {
                const Icon = type.icon;
                const isDisabled = type.disabled || false;
                const isComingSoon = type.comingSoon || false;
                
                return (
                  <button
                    key={type.id}
                    onClick={() => !isDisabled && handleTypeSelect(type.id)}
                    disabled={isDisabled}
                    className={`
                      relative p-4 border rounded-lg flex flex-col items-center text-center transition-all duration-200
                      ${isDisabled 
                        ? 'bg-gray-100 border-gray-200 cursor-not-allowed opacity-60' 
                        : 'bg-white border-gray-300 hover:bg-gray-50 hover:border-gray-400 transform hover:-translate-y-1 hover:shadow-md cursor-pointer'
                      }
                    `}
                  >
                    {isComingSoon && (
                      <span className="absolute top-2 right-2 px-2 py-1 text-xs font-semibold text-white bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full shadow-sm">
                        Coming Soon
                      </span>
                    )}
                    <Icon className={`h-8 w-8 mb-3 ${isDisabled ? 'text-gray-400' : 'text-indigo-600'}`} />
                    <h3 className={`text-sm font-medium mb-1 ${isDisabled ? 'text-gray-500' : 'text-gray-900'}`}>
                      {type.title}
                    </h3>
                    <p className={`text-xs ${isDisabled ? 'text-gray-400' : 'text-gray-500'}`}>
                      {type.description}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        );
        
      case 'salary-form':
        return (
          <SalaryForm 
            bucketId={bucketId}
            income={isEditMode ? income : null}
            isEditing={isEditMode}
            onSuccess={handleSuccess}
            onBack={handleBack}
          />
        );
      
      case 'contractor-form':
        return (
          <ContractorForm
            bucketId={bucketId}
            income={isEditMode ? income : null}
            isEditing={isEditMode}
            onSuccess={handleSuccess}
            onBack={handleBack}
          />
        );
        
      case 'basic-form':
        return (
          <BasicIncomeForm 
            bucketId={bucketId}
            incomeType={selectedType}
            income={isEditMode ? income : null}
            isEditing={isEditMode}
            onSuccess={handleSuccess}
            onBack={handleBack}
          />
        );
        
      default:
        return null;
    }
  };
  
  // Render the appropriate form based on selected income type
  const renderForm = () => {
    switch (selectedType) {
      case 'SALARY':
        return (
          <SalaryForm 
            bucketId={bucketId} 
            onSuccess={onSuccess} 
            onCancel={handleBack}
          />
        );
      case 'CONTRACT':
        return (
          <ContractorForm 
            bucketId={bucketId} 
            onSuccess={onSuccess} 
            onCancel={handleBack}
          />
        );
      default:
        return (
          <BasicIncomeForm 
            bucketId={bucketId} 
            incomeType={selectedType} 
            onSuccess={onSuccess} 
            onCancel={handleBack}
          />
        );
    }
  };
  
  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* Backdrop */}
        <Transition.Child
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        </Transition.Child>
        
        {/* Modal */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-xl bg-white shadow-xl transition-all">
                {/* Header */}
                <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
                  <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900">
                      {getStepTitle()}
                  </Dialog.Title>
                    <button
                      onClick={onClose}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                    >
                    <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                
                {/* Content */}
                <div className="p-6">
                    {renderStep()}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default IncomeTypeWizard; 